import { forwardRef } from 'react';
import {
  Activity,
  BadgeCheck,
  GitBranch,
  Leaf,
  Network,
  Workflow,
} from 'lucide-react';
import { SectionAtmosphere, TYPE_SECTION_EYEBROW, TYPE_LABEL_CAPS } from '../layout/HoldingBackdrop';

interface ServiceItem {
  icon: typeof Activity;
  title: string;
  blurb: string;
  image: string;
  alt: string;
  stack: string[];
}

const OPERATING_PLATFORMS: ServiceItem[] = [
  {
    icon: Activity,
    title: 'Plant performance monitoring',
    blurb:
      'Real-time telemetry, anomaly detection and availability analytics across wind, solar and battery plants — the operating layer above SCADA.',
    image:
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80',
    alt: 'Operator control screen with energy telemetry charts',
    stack: ['Edge → cloud', 'Anomaly detection', 'Availability'],
  },
  {
    icon: Workflow,
    title: 'EMS, SCADA & control integration',
    blurb:
      'Bespoke supervisory layers that integrate plant control, OEM SCADA, grid interfaces and operator workflows into one stack.',
    image:
      'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=900&q=80',
    alt: 'Control electronics close-up, representing EMS and SCADA integration',
    stack: ['EMS', 'OEM SCADA', 'Substation gateways'],
  },
  {
    icon: GitBranch,
    title: 'Dispatch & plant control',
    blurb:
      'Power Plant Controller logic, dispatch and curtailment, shaped around the grid codes each plant has to meet.',
    image:
      'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=900&q=80',
    alt: 'High voltage transmission lines at dusk',
    stack: ['PPC', 'Curtailment', 'Grid code'],
  },
];

const VERIFIABLE_TRUST: ServiceItem[] = [
  {
    icon: BadgeCheck,
    title: 'Generation records',
    blurb:
      'Software that organises meter and generation data into checkable records, including interfaces to the registries operators already use.',
    image:
      'https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=900&q=80',
    alt: 'Wind turbines at sunrise representing renewable generation',
    stack: ['Meter data', 'Generation records', 'Registry interface'],
  },
  {
    icon: Leaf,
    title: 'Smart‑meter data attestation',
    blurb:
      'Signed reads, tamper detection and blockchain anchoring of generation and grid data — field telemetry turned into records operators and auditors can check.',
    image:
      'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=900&q=80',
    alt: 'Photovoltaic panels with technician inspecting',
    stack: ['Signed reads', 'Blockchain anchor', 'Audit feed'],
  },
  {
    icon: Network,
    title: 'Operational records',
    blurb:
      'Software that lines up plant telemetry, dispatch events and field records so operators can review one audit trail.',
    image:
      'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=900&q=80',
    alt: 'Data centre racks with secure infrastructure',
    stack: ['Telemetry', 'Dispatch events', 'Audit trail'],
  },
];

function ServiceCard({ item }: { item: ServiceItem }) {
  const Icon = item.icon;
  return (
    <article className="service-card animate-on-scroll overflow-hidden rounded-3xl border border-[#e8ede4]/10 bg-white/[0.035] transition hover:border-teal-200/35 hover:bg-white/[0.055]">
      <img
        src={item.image}
        alt={item.alt}
        className="service-image opacity-95"
        loading="lazy"
        onError={(e) => {
          // Hide broken images instead of leaking the alt text.
          e.currentTarget.style.display = 'none';
        }}
      />
      <div className="p-8">
        <Icon className="mb-4 h-10 w-10 text-teal-200/85" aria-hidden />
        <h3 className="text-xl font-semibold tracking-[-0.03em] text-[#f1f5ec]/88">
          {item.title}
        </h3>
        <p className="mt-4 leading-7 text-[#d6ddcf]/85">{item.blurb}</p>
        <ul className="mt-5 flex flex-wrap gap-2">
          {item.stack.map((tag) => (
            <li
              key={tag}
              className="rounded-full border border-[#e8ede4]/10 bg-black/15 px-3 py-1 text-xs text-[#d6ddcf]/85"
            >
              {tag}
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

const Services = forwardRef<HTMLElement>((_, ref) => {
  return (
    <section ref={ref} id="services" className="relative isolate overflow-hidden px-5 py-24 md:px-8 md:py-32">
      <SectionAtmosphere variant="services" />
      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="animate-on-scroll mx-auto mb-16 max-w-3xl text-center">
          <p className={TYPE_SECTION_EYEBROW}>Solutions</p>
          <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-[#f1f5ec]/90 md:text-5xl">
            Two layers, one platform
          </h2>
          <p className="mt-6 text-base leading-8 text-[#d6ddcf]/85 md:text-lg">
            Software for power plants and a verification layer for generation data — built for
            renewable operators. We integrate with the SCADA and EMS stacks your team already uses,
            instead of replacing them.
          </p>
        </div>

        <p className={`${TYPE_LABEL_CAPS} mb-8`}>Operating platforms</p>
        <div className="mb-20 grid gap-6 md:grid-cols-3 md:gap-5">
          {OPERATING_PLATFORMS.map((item) => (
            <ServiceCard key={item.title} item={item} />
          ))}
        </div>

        <p className={`${TYPE_LABEL_CAPS} mb-8`}>Verifiable trust</p>
        <div className="grid gap-6 md:grid-cols-3 md:gap-5">
          {VERIFIABLE_TRUST.map((item) => (
            <ServiceCard key={item.title} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
});

Services.displayName = 'Services';
export default Services;
